import type { WModuleView } from '@zola/svc-web-api-ts-client';
import { notEmpty } from 'libs/universal/utils/type';
import { Fragment } from 'react';
import Imgix, { Picture, Source } from 'react-imgix';

type WidthImage = {
  width: number;
  image: string;
};

export function getWidthImages(imagesByDimension: { [dimension: string]: string }): WidthImage[] {
  return Object.keys(imagesByDimension)
    .map(k => {
      const [w] = k.split('x');
      return { width: Number(w), image: imagesByDimension[k] };
    })
    .sort((a, b) => a.width - b.width); // sort asc
}

type SourceType = {
  media: string;
  srcSet: string;
};

export function getSources(widthImages: WidthImage[], breakpointPxs: number[]): SourceType[] {
  const sortedWidthImages = widthImages.sort((a, b) => a.width - b.width); // sort asc
  const sources = breakpointPxs
    .sort((a, b) => a - b)
    .map(bPx => {
      const [widthImage] = sortedWidthImages.filter(wi => wi.width >= bPx);
      if (!widthImage) {
        return null;
      }
      return { media: `(min-width: ${bPx}px)`, srcSet: widthImage.image };
    })
    .filter(notEmpty);

  // filter duplicate
  return sources.filter((s, i) => i === sources.map(s2 => s2.srcSet).indexOf(s.srcSet)).reverse();
}

export function wModuleViewToResponsivePicture(
  module: WModuleView,
  breakpointPxs: number[]
): JSX.Element | null {
  const imagesByDimension = module.images_by_dimension;
  if (!imagesByDimension) {
    return null;
  }

  const widthImages = getWidthImages(imagesByDimension);

  if (widthImages.length === 0) {
    return null;
  }

  const sources = getSources(
    widthImages,
    breakpointPxs.sort((a, b) => a - b) // sort asc
  ).map((s, i) => (
    <Source
      key={`src-${i}`}
      htmlAttributes={{
        media: s.media,
      }}
      src={s.srcSet}
    />
  ));

  const narrowestImage = widthImages[0].image;

  return (
    <Picture>
      {sources.length ? <Fragment>{sources}</Fragment> : <Fragment />}
      <Imgix
        src={narrowestImage}
        htmlAttributes={{
          alt: module.description || '',
        }}
      />
    </Picture>
  );
}
